import { useCallback, useState } from "react";
import { TechnologyService } from "../../../../../services/technologyService";
import {
  WorkExperience,
  WorkExperienceCreateUpdate,
} from "../models/workExperience.model";
import { WorkExperienceService } from "../services/workExperienceService";

const technologyService = new TechnologyService();

interface UseWorkExperienceProps {
  userId: number;
  initialWorkExperiences?: WorkExperience[];
  onError?: () => void;
}

interface UseWorkExperienceReturn {
  workExperiences: WorkExperience[];
  isLoading: boolean;
  addWorkExperience: (
    workExperience: WorkExperienceCreateUpdate
  ) => Promise<void>;
  updateWorkExperience: (
    id: number,
    workExperience: WorkExperienceCreateUpdate
  ) => Promise<void>;
  deleteWorkExperience: (id: number) => Promise<void>;
  refreshWorkExperiences: () => Promise<void>;
  getEmployersDictionary: () => Promise<string[]>;
  getTechnologiesDictionary: () => Promise<any[]>;
}

export function useWorkExperience({
  userId,
  initialWorkExperiences = [],
  onError,
}: UseWorkExperienceProps): UseWorkExperienceReturn {
  const [workExperiences, setWorkExperiences] = useState<WorkExperience[]>(
    initialWorkExperiences
  );
  const [isLoading, setIsLoading] = useState(false);

  const workExperienceService = new WorkExperienceService();
  const technologyService = new TechnologyService();

  const refreshWorkExperiences = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      const data = await workExperienceService.getWorkItemsByUserID(userId);
      setWorkExperiences(data);
    } catch (error) {
      console.error("Error refreshing work experiences:", error);
      if (onError) onError();
    } finally {
      setIsLoading(false);
    }
  }, [userId, onError]);

  const addWorkExperience = useCallback(
    async (workExperience: WorkExperienceCreateUpdate) => {
      setIsLoading(true);
      try {
        const newWorkExperience = await workExperienceService.addWorkExperience(
          {
            ...workExperience,
            personId: userId,
          }
        );

        setWorkExperiences((prev) => [...prev, newWorkExperience]);
      } catch (error) {
        console.error("Error adding work experience:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  const updateWorkExperience = useCallback(
    async (id: number, workExperience: WorkExperienceCreateUpdate) => {
      setIsLoading(true);
      try {
        const updatedWorkExperience =
          await workExperienceService.updateWorkExperience(id, {
            ...workExperience,
            personId: userId,
          });

        setWorkExperiences((prev) =>
          prev.map((we) => (we.id === id ? updatedWorkExperience : we))
        );
      } catch (error) {
        console.error("Error updating work experience:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  const deleteWorkExperience = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      await workExperienceService.deleteWorkExperience(id);

      setWorkExperiences((prev) => prev.filter((we) => we.id !== id));
    } catch (error) {
      console.error("Error deleting work experience:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getEmployersDictionary = useCallback(async (): Promise<string[]> => {
    try {
      return await workExperienceService.getEmployersDictionary();
    } catch (error) {
      console.error("Error getting employers dictionary:", error);
      return [];
    }
  }, []);

  const getTechnologiesDictionary = useCallback(async (): Promise<any[]> => {
    try {
      return await technologyService.getTechnologiesDictionary();
    } catch (error) {
      console.error("Error getting technologies dictionary:", error);
      return [];
    }
  }, []);

  return {
    workExperiences,
    isLoading,
    addWorkExperience,
    updateWorkExperience,
    deleteWorkExperience,
    refreshWorkExperiences,
    getEmployersDictionary,
    getTechnologiesDictionary,
  };
}
